import { useMutation, useQuery } from "@tanstack/react-query";

import { api, localStorageService } from "@services/index";
import {
  ApiErrorModel,
  TokensDto,
  LoginFormInterface,
  PasswordForgotFormInterface,
  PasswordResetFormInterface,
  PasswordResetFormInterfaceDto,
  UserModelDto,
  UserModel,
  RegisterFormInterface,
  RegisterFormInterfaceDto,
  LeaveFormInterface,
  LeaveFormInterfaceDto,
  ChangeNameFormInterface,
  ChangeNameFormInterfaceDto,
  ChangePhoneFormInterface,
  ChangePhoneFormInterfaceDto,
  ChangePasswordFormInterface,
  ChangePasswordFormInterfaceDto,
  AcceptInvitationFormInterface,
  AcceptInvitationFormInterfaceDto,
  ReportProblemFormInterface,
  ReportProblemFormInterfaceDto,
  RequestCallFormInterface,
  RequestCallFormInterfaceDto,
  UserPeriods,
  UserPeriodsDto,
} from "@interfaces/index";

export const useUserLogin = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async (fields: LoginFormInterface) => {
    const { data } = await api.post<TokensDto>("/login", fields);
    localStorageService.setTokens(data.token, data.refresh_token);
  };

  return useMutation({
    mutationKey: ["userLogin"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        email: error.fields?.email || "",
        password: error.fields?.password || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserLogout = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async () => {
    await api.post("/logout");
    localStorageService.clearTokens();
  };

  return useMutation({
    mutationKey: ["userLogout"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useUserPasswordForgot = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) =>
  useMutation({
    mutationKey: ["userPasswordForgot"],
    mutationFn: (fields: PasswordForgotFormInterface) =>
      api.post("/forgot-password", fields),
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        email: error.fields?.email || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });

export const useUserPasswordReset = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    email,
    password,
    confirmPassword,
    code,
  }: PasswordResetFormInterface) => {
    const fieldsDto: PasswordResetFormInterfaceDto = {
      email,
      password,
      password_confirmation: confirmPassword,
      token: code,
    };
    await api.post("/reset-password", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userPasswordReset"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        password: error.fields?.password || "",
        confirmPassword: error.fields?.password_confirmation || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserEmailRequest = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) =>
  useMutation({
    mutationKey: ["userEmailRequest"],
    mutationFn: () => api.post("/email/verification-notification"),
    onSuccess,
    onError,
  });

export const useUserEmailVerify = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    id,
    hash,
    query,
  }: {
    id: string;
    hash: string;
    query: string;
  }) => {
    await api.get(`/verify-email/${id}/${hash}${query}`);
  };

  return useMutation({
    mutationKey: ["userEmailVerify"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useUserRegister = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    firstName,
    lastName,
    email,
    phone,
    businessName,
  }: RegisterFormInterface) => {
    const fieldsDto: RegisterFormInterfaceDto = {
      first_name: firstName,
      last_name: lastName,
      email,
      phone,
      business_name: businessName,
    };
    await api.post("/request-registration", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userRegister"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        firstName: error.fields?.first_name || "",
        lastName: error.fields?.last_name || "",
        email: error.fields?.email || "",
        phone: error.fields?.phone || "",
        businessName: error.fields?.business_name || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserLeave = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({ message }: LeaveFormInterface) => {
    const fieldsDto: LeaveFormInterfaceDto = {
      reason: message,
      phone_call_requested: false,
    };
    await api.post("/request-leave", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userLeave"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        message: error.fields?.reason || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserAcceptInvitation = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    code,
    email,
    password,
    confirmPassword,
  }: AcceptInvitationFormInterface) => {
    const fieldsDto: AcceptInvitationFormInterfaceDto = {
      email,
      password,
      password_confirmation: confirmPassword,
      token: code,
    };
    await api.post("/accept-invitation", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userAcceptInvitation"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        password: error.fields?.password || "",
        confirmPassword: error.fields?.password_confirmation || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserGetProfile = (enabled: boolean = true) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<UserModelDto>("/user");
    const data: UserModel = {
      uniqueId: dataDto.unique_id,
      email: dataDto.email || "",
      emailVerifiedAt: dataDto.email_verified_at || "",
      firstName: dataDto.first_name || "",
      lastName: dataDto.last_name || "",
      fullName: dataDto.full_name || "",
      phone: dataDto.phone || "",
      clientCode: dataDto.client_code || "",
      businesses: dataDto.businesses.map(
        ({ name, unique_id, type, trading_start_date }) => ({
          name,
          uniqueId: unique_id,
          type,
          tradingStartDate: trading_start_date,
        }),
      ),
      defaultBusiness: {
        name: dataDto.default_business.name,
        uniqueId: dataDto.default_business.unique_id,
        type: dataDto.default_business.type,
        tradingStartDate: dataDto.default_business.trading_start_date,
      },
      puEnabled: !!dataDto.pu_enabled,
      disabledFeatures: dataDto.disabled_features?.length
        ? dataDto.disabled_features
        : [],
      redConnectedBusinessId: dataDto.red_connected_business_id || "",
      queriesCount: dataDto.queries_count || 0,
    };

    return data;
  };

  return useQuery<UserModel, ApiErrorModel>({
    queryKey: ["userGetProfile"],
    queryFn,
    enabled,
  });
};

export const useUserChangeName = (
  onSuccess?: (data: ChangeNameFormInterface) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    firstName,
    lastName,
  }: ChangeNameFormInterface) => {
    const fieldsDto: ChangeNameFormInterfaceDto = {
      first_name: firstName,
      last_name: lastName,
    };
    const res = await api.post("/user/profile", fieldsDto);

    return {
      firstName: res.data.first_name,
      lastName: res.data.last_name,
    };
  };

  return useMutation({
    mutationKey: ["userChangeName"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        firstName: error.fields?.first_name || "",
        lastName: error.fields?.last_name || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserChangePhone = (
  onSuccess?: (data: ChangePhoneFormInterface) => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({ phone }: ChangePhoneFormInterface) => {
    const fieldsDto: ChangePhoneFormInterfaceDto = { phone };
    const res = await api.post("/user/profile", fieldsDto);

    return { phone: res.data.phone };
  };

  return useMutation({
    mutationKey: ["userChangePhone"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        phone: error.fields?.phone || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserChangePassword = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    currentPassword,
    password,
    confirmPassword,
  }: ChangePasswordFormInterface) => {
    const fieldsDto: ChangePasswordFormInterfaceDto = {
      current_password: currentPassword,
      password,
      password_confirmation: confirmPassword,
    };
    await api.post("/update-password", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userChangePassword"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        currentPassword: error.fields?.current_password || "",
        password: error.fields?.password || "",
        confirmPassword: error.fields?.password_confirmation || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserUpdateDefaultBusiness = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) =>
  useMutation({
    mutationKey: ["userUpdateDefaultBusiness"],
    mutationFn: (uniqueId: string) =>
      api.post(`/user/businesses/${uniqueId}/set-default`),
    onSuccess,
    onError,
  });

export const useUserReportProblem = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    phone,
    message,
    userId,
    browserVersion,
    files,
  }: ReportProblemFormInterface) => {
    const fieldsDto: ReportProblemFormInterfaceDto = {
      phone,
      message,
      user_id: userId,
      browser_version: browserVersion,
      files: files?.map((file) => ({
        key: file.key,
        uuid: file.uuid,
        original_name: file.originalName,
      })),
    };
    await api.post("/report-problem", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userReportProblem"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        phone: error.fields?.phone || "",
        message: error.fields?.message || "",
        userId: error.fields?.user_id || "",
        browserVersion: error.fields?.browser_version || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserRequestCall = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async ({
    phone,
    message,
    userId,
    browserVersion,
  }: RequestCallFormInterface) => {
    const fieldsDto: RequestCallFormInterfaceDto = {
      phone,
      message,
      user_id: userId,
      browser_version: browserVersion,
    };
    await api.post("/request-call", fieldsDto);
  };

  return useMutation({
    mutationKey: ["userRequestCall"],
    mutationFn,
    onSuccess,
    onError: (error: ApiErrorModel) => {
      const fields: Record<string, string> = {
        phone: error.fields?.phone || "",
        message: error.fields?.message || "",
        userId: error.fields?.user_id || "",
        browserVersion: error.fields?.browser_version || "",
      };
      if (onError) {
        onError({ ...error, fields });
      }
    },
  });
};

export const useUserGetPeriods = ({ enabled }: { enabled: boolean }) => {
  const queryFn = async () => {
    const { data: dataDto } = await api.get<UserPeriodsDto>("/app-settings");
    const data: UserPeriods = {
      currentPeriod: dataDto.current_period
        ? {
            title: dataDto.current_period.title,
            status: {
              label: dataDto.current_period.status.label,
              value: dataDto.current_period.status.value,
            },
            startDate: dataDto.current_period.start_date,
            endDate: dataDto.current_period.end_date,
          }
        : undefined,
      periods: dataDto.periods.map((period) => ({
        title: period.title,
        status: {
          label: period.status.label,
          value: period.status.value,
        },
        startDate: period.start_date,
        endDate: period.end_date,
      })),
    };

    return data;
  };

  return useQuery<UserPeriods, ApiErrorModel>({
    queryKey: ["userGetPeriods"],
    queryFn,
    enabled,
  });
};

export const useUserGetOpenPeriods = () => {
  const queryFn = async () => {
    const { data: dataDto } =
      await api.get<UserPeriodsDto>("/tax-year-periods");
    const data: UserPeriods = {
      currentPeriod: dataDto.current_period
        ? {
            title: dataDto.current_period.title,
            status: {
              label: dataDto.current_period.status.label,
              value: dataDto.current_period.status.value,
            },
            startDate: dataDto.current_period.start_date,
            endDate: dataDto.current_period.end_date,
          }
        : undefined,
      periods: dataDto.periods.map((period) => ({
        title: period.title,
        status: {
          label: period.status.label,
          value: period.status.value,
        },
        startDate: period.start_date,
        endDate: period.end_date,
      })),
    };

    return data;
  };

  return useQuery<UserPeriods, ApiErrorModel>({
    queryKey: ["userGetOpenPeriods"],
    queryFn,
  });
};

export const useUserConnectRedStatement = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async (userBusinessId: string) => {
    await api.post("/user/external-providers/red-connection", {
      user_business_unique_id: userBusinessId,
    });
  };

  return useMutation({
    mutationKey: ["userConnectRedStatement"],
    mutationFn,
    onSuccess,
    onError,
  });
};

export const useUserDisconnectRedStatement = (
  onSuccess?: () => void,
  onError?: (error: ApiErrorModel) => void,
) => {
  const mutationFn = async () => {
    await api.delete("/user/external-providers/red-connection");
  };

  return useMutation({
    mutationKey: ["userDisconnectRedStatement"],
    mutationFn,
    onSuccess,
    onError,
  });
};
